import { jsx } from 'react/jsx-runtime';
import React, { useState, useCallback, useEffect, useContext } from 'react';
import config from '@sketch/env-config';
import { noop } from '@sketch/utils';
import { useGetUserHubSpotIdentificationTokenLazyQuery } from '@sketch/gql-types';

const parseHubsJson = (jsonString) => {
    try {
        const o = JSON.parse(jsonString);
        if (o && typeof o === 'object') {
            return o;
        }
    }
    catch (e) {
        return false;
    }
    return false;
};
const HubspotChatContext = React.createContext({
    loaded: false,
    loadChat: noop,
    unloadChat: noop,
});
const HubspotChatProvider = ({ children }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [getHubspotIdentificationToken,] = useGetUserHubSpotIdentificationTokenLazyQuery({
        fetchPolicy: 'network-only',
        onError: () => {
            var _a;
            window.hsConversationsSettings = {
                loadImmediately: false,
            };
            (_a = window.HubSpotConversations) === null || _a === void 0 ? void 0 : _a.widget.load({
                widgetOpen: false,
            });
        },
        onCompleted: data => {
            var _a, _b;
            window.hsConversationsSettings = {
                identificationEmail: ((_a = data.me) === null || _a === void 0 ? void 0 : _a.email) || '',
                identificationToken: data.me.hsIdentificationToken,
                loadImmediately: false,
            };
            (_b = window.HubSpotConversations) === null || _b === void 0 ? void 0 : _b.widget.load({
                widgetOpen: false,
            });
        },
    });
    const onHubsMessage = useCallback((e) => {
        // Once open, the chat widget never goes away:
        // when the user hits the close "X" button, it gets minimized instead of being closed and removed from the screen
        // so we alter this by hooking into the messages the hubspot iframe emits when it minimizes the chat
        if (e.origin === 'https://app.hubspot.com') {
            const hubsLoad = parseHubsJson(e.data);
            if (hubsLoad &&
                hubsLoad.type === 'open-change' &&
                !hubsLoad.data.isOpen)
                unloadChat();
        }
        return;
    }, []);
    useEffect(() => {
        // Add event listener.
        window.hsConversationsOnReady = [
            () => {
                if (!isLoaded) {
                    getHubspotIdentificationToken();
                    setIsLoaded(true);
                }
            },
        ];
        return () => {
            window.hsConversationsOnReady = [];
        };
    }, [getHubspotIdentificationToken, isLoaded]);
    useEffect(() => {
        isLoaded && window.addEventListener('message', onHubsMessage);
        return () => {
            window.removeEventListener('message', onHubsMessage);
        };
    }, [isLoaded, onHubsMessage]);
    const loadChat = useCallback(() => {
        window.hsConversationsSettings = {
            loadImmediately: false,
        };
        // The src attribute with the double forward slash is what's called a protocol-independent absolute path
        // and was taken from the HubSpot configuration instructions in case it interests to anyone it defaults the
        // scheme component to the one used by the browsers parent HTTPS in our case
        // see https://www.rfc-editor.org/rfc/rfc2396#section-5.2
        // documenting it here since the question popped up in the CR and we tough it may be interesting for anyone curious
        const src = `//js.hs-scripts.com/${config['hubspot_portal_id']}.js`;
        let script = document.querySelector(`script[src="${src}"]`);
        if (window.HubSpotConversations &&
            !window.HubSpotConversations.widget.status().loaded) {
            // if the script was already loaded we need to get the identification token again
            // since the user may have logged out and logged in with another one
            getHubspotIdentificationToken();
        }
        if (!script) {
            script = document.createElement('script');
            script.setAttribute('src', src);
            document.body.appendChild(script);
        }
    }, [getHubspotIdentificationToken]);
    const unloadChat = () => {
        var _a, _b;
        (_a = window.HubSpotConversations) === null || _a === void 0 ? void 0 : _a.widget.remove();
        (_b = window.HubSpotConversations) === null || _b === void 0 ? void 0 : _b.clear({ resetWidget: true });
        window.hsConversationsSettings = {
            loadImmediately: false,
        };
        setIsLoaded(false);
    };
    return (jsx(HubspotChatContext.Provider, Object.assign({ value: { loaded: isLoaded, unloadChat, loadChat } }, { children: children })));
};
const useHubspotChat = () => {
    const { loaded, loadChat, unloadChat } = useContext(HubspotChatContext);
    return {
        loadChat,
        unloadChat,
        isLoaded: loaded,
    };
};

export { HubspotChatContext, HubspotChatProvider, useHubspotChat };
